import { Injectable } from '@angular/core';
import { StoredEntityData, UserDraftIndex } from './storage.interface';

type AuthAccountAccessor = {
	instance?: {
		getActiveAccount?: () => { localAccountId?: string } | null;
		getAllAccounts?: () => { localAccountId?: string }[];
	};
} | null;

@Injectable()
export class LocalStorageService {
	private readonly CURRENT_USER_ID_KEY = 'app_current_user_id';

	/**
	 * Retrieves an item from local storage and deserializes it.
	 * @param key The key of the item to retrieve.
	 * @returns The deserialized item, or null if not found or on error.
	 */
	public getItem<T>(key: string): T | undefined {
		try {
			const item = localStorage.getItem(key);
			return item ? JSON.parse(item) : undefined;
		} catch (error) {
			console.error(`Error reading from local storage for key: ${key}`, error);
			return;
		}
	}

	/**
	 * Serializes and saves an item to local storage.
	 * @param key The key to store the item under.
	 * @param value The value to store.
	 */
	public setItem<T>(key: string, value: T) {
		try {
			const item = JSON.stringify(value);
			localStorage.setItem(key, item);
		} catch (error) {
			console.error(`Error writing to local storage for key: ${key}`, error);
		}
	}

	/**
	 * Removes an item from local storage.
	 * @param key The key of the item to remove.
	 */
	public removeItem(key: string) {
		try {
			localStorage.removeItem(key);
		} catch (error) {
			console.error(`Error removing item from local storage for key: ${key}`, error);
		}
	}

	/**
	 * Removes every item whose key matches the predicate.
	 * @param shouldRemove Returns true for keys that should be removed.
	 */
	public removeWhere(shouldRemove: (key: string) => boolean) {
		try {
			const keys: string[] = [];
			for (let index = 0; index < localStorage.length; index++) {
				const key = localStorage.key(index);
				if (key !== null && shouldRemove(key)) keys.push(key);
			}
			keys.forEach((key) => this.removeItem(key));
		} catch (error) {
			console.error('Error removing items from local storage', error);
		}
	}

	/**
	 * Clears all items from local storage.
	 */
	public clear() {
		try {
			localStorage.clear();
		} catch (error) {
			console.error('Error clearing local storage', error);
		}
	}

	/**
	 * Generates a storage key for a draft.
	 * @param userId The user's ID.
	 * @param entityType The type of the entity.
	 * @param entityId The entity's ID.
	 * @returns The generated storage key.
	 */
	public generateDraftKey(userId: string, entityType: string, entityId: string) {
		return `lvr_draft_${userId}_${entityType}_${entityId}`;
	}

	/**
	 * Generates the storage key for the user's draft index.
	 * @param userId The user's ID.
	 * @returns The generated storage key.
	 */
	public generateIndexKey(userId: string) {
		return `lvr_index_${userId}`;
	}

	/**
	 * Retrieves the user's draft index.
	 * @param userId The user's ID.
	 * @returns The user's draft index, or null if not found.
	 */
	public getUserDraftIndex(userId: string) {
		const key = this.generateIndexKey(userId);
		return this.getItem<UserDraftIndex>(key);
	}

	/**
	 * Updates the user's draft index.
	 * @param userId The user's ID.
	 * @param index The updated draft index.
	 */
	public setUserDraftIndex(userId: string, index: UserDraftIndex) {
		const key = this.generateIndexKey(userId);
		this.setItem(key, index);
	}

	/**
	 * Retrieves a specific draft from storage.
	 * @param key The key of the draft to retrieve.
	 * @returns The stored entity data, or null if not found.
	 */
	public getDraft<T>(key: string) {
		return this.getItem<StoredEntityData<T>>(key);
	}

	/**
	 * Saves a draft to storage.
	 * @param key The key to store the draft under.
	 * @param data The draft data to store.
	 */
	public setDraft(key: string, data: StoredEntityData) {
		this.setItem(key, data);
	}

	/**
	 * Generates a storage key for paginator page size preference.
	 * @param userId The user's ID.
	 * @param contextKey A unique context identifier (e.g., 'sponsor-persons-list', 'user-list').
	 * @returns The generated storage key.
	 */
	public generatePageSizeKey(userId: string, contextKey: string) {
		return `lvr_paginator_page_size_${userId}_${contextKey}`;
	}

	public setCurrentUserId(userId: string) {
		this.setItem(this.CURRENT_USER_ID_KEY, userId);
	}

	public clearCurrentUserId() {
		this.removeItem(this.CURRENT_USER_ID_KEY);
	}

	public resolveCurrentUserId(auth?: AuthAccountAccessor) {
		const activeAccountId = auth?.instance?.getActiveAccount?.()?.localAccountId;
		if (activeAccountId) return activeAccountId;

		const accountId = auth?.instance?.getAllAccounts?.()?.[0]?.localAccountId;
		if (accountId) return accountId;

		return this.getItem<string>(this.CURRENT_USER_ID_KEY);
	}

	/**
	 * Saves the last chosen items per page value of paginator to storage.
	 * @param userId The user's ID.
	 * @param contextKey A unique context identifier (e.g., 'sponsor-persons-list', 'user-list').
	 * @param pageSize The page size to store.
	 */
	public setPageSize(userId: string, contextKey: string, pageSize: number) {
		const key = this.generatePageSizeKey(userId, contextKey);
		this.setItem(key, pageSize);
	}

	/**
	 * Retrieves the last chosen items per page value of paginator from storage.
	 * @param userId The user's ID.
	 * @param contextKey A unique context identifier (e.g., 'sponsor-persons-list', 'user-list').
	 * @returns The stored page size, or undefined if not found.
	 */
	public getPageSize(userId: string, contextKey: string) {
		const key = this.generatePageSizeKey(userId, contextKey);
		return this.getItem<number>(key);
	}
}
